import { a as formatPriceWithoutTrailingZeros } from "@theme/utils";
//#region frontend/islands/product-recipe-picker.js
var ProductRecipePicker = class extends HTMLElement {
	connectedCallback() {
		this.optionName = this.dataset.optionName;
		this.optionIndex = parseInt(this.dataset.optionIndex, 10);
		this.querySelectorAll("[data-recipe-value]").forEach((el) => {
			const event = el.tagName === "INPUT" ? "change" : "click";
			el.addEventListener(event, () => this.selectRecipe(el.dataset.recipeValue));
		});
		document.addEventListener("recipe:change", (evt) => {
			this.updateActiveState(evt.detail.recipeName, { fromQuiz: !!evt.detail.fromQuiz });
		});
		this.init();
		this.initRovingTabindex();
	}
	initRovingTabindex() {
		const buttons = [...this.querySelectorAll("button[data-recipe-value]")];
		buttons.forEach((btn) => {
			btn.addEventListener("keydown", (e) => {
				const idx = buttons.indexOf(btn);
				let target = null;
				if (e.key === "ArrowRight" || e.key === "ArrowDown") {
					e.preventDefault();
					target = buttons[(idx + 1) % buttons.length];
				} else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
					e.preventDefault();
					target = buttons[(idx - 1 + buttons.length) % buttons.length];
				} else if (e.key === "Home") {
					e.preventDefault();
					target = buttons[0];
				} else if (e.key === "End") {
					e.preventDefault();
					target = buttons[buttons.length - 1];
				}
				if (target) {
					buttons.forEach((b) => b.setAttribute("tabindex", "-1"));
					target.setAttribute("tabindex", "0");
					target.focus();
					this.selectRecipe(target.dataset.recipeValue);
				}
			});
		});
	}
	init() {
		const variantRadios = this.getVariantRadios();
		if (!variantRadios) return;
		const checkedRadio = variantRadios.querySelector(`input[name="${CSS.escape(this.optionName)}"]:checked`);
		if (!checkedRadio) return;
		this.updateActiveState(checkedRadio.value);
		const variants = JSON.parse(variantRadios.querySelector("[type=\"application/json\"]").textContent);
		this.updateSizeAvailability(variantRadios, checkedRadio.value, variants);
		this.updateSizeLabels(variantRadios, checkedRadio.value, variants);
	}
	getVariantRadios() {
		return document.querySelector("variant-radios");
	}
	selectRecipe(value) {
		const variantRadios = this.getVariantRadios();
		if (!variantRadios) return;
		const radio = variantRadios.querySelector(`input[name="${CSS.escape(this.optionName)}"][value="${CSS.escape(value)}"]`);
		if (!radio || radio.checked) return;
		radio.checked = true;
		radio.dispatchEvent(new Event("change", { bubbles: true }));
		const variants = JSON.parse(variantRadios.querySelector("[type=\"application/json\"]").textContent);
		this.updateSizeAvailability(variantRadios, value, variants);
		this.updateSizeLabels(variantRadios, value, variants);
		this.updateSizeButtons(variantRadios, value, variants);
		this.updateActiveState(value);
		document.dispatchEvent(new CustomEvent("recipe:change", { detail: {
			recipeName: value,
			variant: variantRadios.currentVariant
		} }));
		const drawer = this.closest("product-alternatives-drawer");
		if (drawer) setTimeout(() => drawer.close(), 150);
	}
	updateActiveState(selectedRecipe, { fromQuiz = false } = {}) {
		if (!selectedRecipe) return;
		const label = this.querySelector("p > span");
		if (label) label.textContent = selectedRecipe;
		this.querySelectorAll("[data-recipe-value]").forEach((el) => {
			const isSelected = el.dataset.recipeValue === selectedRecipe;
			if (el.tagName === "INPUT") el.checked = isSelected;
			else {
				el.setAttribute("aria-checked", isSelected ? "true" : "false");
				el.setAttribute("tabindex", isSelected ? "0" : "-1");
			}
		});
		if (this.closest("product-alternatives-drawer") && fromQuiz) this._syncDrawerSections(selectedRecipe);
	}
	_syncDrawerSections(selectedRecipe) {
		const chefSection = this.querySelector("[data-chef-section]");
		const carteSection = this.querySelector("[data-carte-section]");
		if (!chefSection || !carteSection) return;
		const chefLabel = chefSection.querySelector("label");
		const chefRecipe = chefLabel?.querySelector("input[data-recipe-value]")?.dataset.recipeValue;
		if (!chefRecipe || chefRecipe === selectedRecipe) return;
		const selectedLabel = carteSection.querySelector(`label:has(input[data-recipe-value="${CSS.escape(selectedRecipe)}"])`);
		if (!selectedLabel) return;
		const divider = chefSection.querySelector("[data-chef-divider]");
		divider ? divider.after(selectedLabel) : chefSection.prepend(selectedLabel);
		carteSection.appendChild(chefLabel);
	}
	updateSizeAvailability(variantRadios, selectedRecipe, variants) {
		const sizeOptionIndex = this.optionIndex === 0 ? 1 : 0;
		const recipeVariants = variants.filter((v) => v.options[this.optionIndex] === selectedRecipe);
		const availableSizes = new Set(recipeVariants.filter((v) => v.available).map((v) => v.options[sizeOptionIndex]));
		const sizeFieldset = Array.from(variantRadios.querySelectorAll("fieldset"))[sizeOptionIndex];
		if (!sizeFieldset) return;
		let currentSizeAvailable = false;
		sizeFieldset.querySelectorAll("input[type=\"radio\"]").forEach((radio) => {
			const isAvailable = availableSizes.has(radio.value);
			radio.disabled = !isAvailable;
			const wrapper = radio.closest("div");
			if (wrapper) wrapper.hidden = !isAvailable;
			if (radio.checked && isAvailable) currentSizeAvailable = true;
		});
		if (!currentSizeAvailable) {
			const firstAvailable = sizeFieldset.querySelector("input[type=\"radio\"]:not(:disabled)");
			if (firstAvailable) {
				firstAvailable.checked = true;
				firstAvailable.dispatchEvent(new Event("change", { bubbles: true }));
			}
		}
	}
	updateSizeButtons(variantRadios, selectedRecipe, variants) {
		const metafieldsEl = variantRadios.querySelector("[data-variant-metafields]");
		if (!metafieldsEl) return;
		const metafields = JSON.parse(metafieldsEl.textContent);
		const sizeOptionIndex = this.optionIndex === 0 ? 1 : 0;
		const sizeFieldset = Array.from(variantRadios.querySelectorAll("fieldset"))[sizeOptionIndex];
		if (!sizeFieldset) return;
		sizeFieldset.querySelectorAll("input[type=\"radio\"]").forEach((radio) => {
			const targetVariant = variants.find((v) => v.options[this.optionIndex] === selectedRecipe && v.options[sizeOptionIndex] === radio.value);
			if (!targetVariant) return;
			const meta = metafields[String(targetVariant.id)] ?? {};
			const wrapper = sizeFieldset.querySelector(`[data-size-option="${CSS.escape(radio.value)}"]`);
			if (!wrapper) return;
			const priceEl = wrapper.querySelector("[data-size-price]");
			if (priceEl) {
				const price = targetVariant.price;
				const comparePrice = targetVariant.compare_at_price;
				if (comparePrice && comparePrice > price) priceEl.innerHTML = `<span class="text-green">${formatPriceWithoutTrailingZeros(price)}</span> <span class="strike opacity-50">${formatPriceWithoutTrailingZeros(comparePrice)}</span>`;
				else priceEl.textContent = formatPriceWithoutTrailingZeros(price);
			}
			const badgeEl = wrapper.querySelector("[data-size-badge]");
			if (badgeEl) {
				const badgeText = meta.badge ?? "";
				const accent = meta.accent_color ?? "mauve";
				if (badgeText) {
					badgeEl.textContent = badgeText;
					badgeEl.className = badgeEl.className.replace(/\bbg-\S+/, `bg-${accent}`);
					badgeEl.classList.remove("invisible");
					badgeEl.removeAttribute("aria-hidden");
				} else {
					badgeEl.textContent = " ";
					badgeEl.className = badgeEl.className.replace(/\bbg-\S+/, "");
					badgeEl.classList.add("invisible");
					badgeEl.setAttribute("aria-hidden", "true");
				}
			}
		});
	}
	updateSizeLabels(variantRadios, selectedRecipe, variants) {
		const metafieldsEl = variantRadios.querySelector("[data-variant-metafields]");
		if (!metafieldsEl) return;
		const metafields = JSON.parse(metafieldsEl.textContent);
		const sizeOptionIndex = this.optionIndex === 0 ? 1 : 0;
		const sizeFieldset = Array.from(variantRadios.querySelectorAll("fieldset"))[sizeOptionIndex];
		if (!sizeFieldset) return;
		sizeFieldset.querySelectorAll("input[type=\"radio\"]").forEach((radio) => {
			const targetVariant = variants.find((v) => v.options[this.optionIndex] === selectedRecipe && v.options[sizeOptionIndex] === radio.value);
			if (!targetVariant) return;
			const meals = metafields[String(targetVariant.id)]?.number_of_units ?? null;
			const label = sizeFieldset.querySelector(`label[for="${radio.id}"]`);
			if (!label) return;
			const mealsSpan = label.querySelector("[data-variant-meals]");
			if (!mealsSpan) return;
			if (meals != null) {
				mealsSpan.textContent = `(${meals} meals)`;
				mealsSpan.hidden = false;
			} else {
				mealsSpan.textContent = "";
				mealsSpan.hidden = true;
			}
		});
	}
};
customElements.define("product-recipe-picker", ProductRecipePicker);
//#endregion
